/**
 * Custom hook for managing textarea ref operations safely
 * Provides consistent null-checking and common operations
 * Issue #11: Inconsistent Null Checks for inputRef
 */

import { useRef, useCallback } from "react";

/**
 * Custom hook for managing textarea ref operations safely
 * @param {Object} options - Configuration options
 * @returns {Object} - Ref and helper functions
 */
export const useTextareaRef = (options = {}) => {
    const { minHeight = 36, maxHeight = 110 } = options;

    const inputRef = useRef(null);

    /**
     * Safely get the textarea element
     * @returns {HTMLTextAreaElement|null}
     */
    const getTextarea = useCallback(() => inputRef.current, []);

    /**
     * Check if textarea ref is available
     * @returns {boolean}
     */
    const isAvailable = useCallback(() => inputRef.current !== null, []);

    /**
     * Reset textarea height to minimum
     */
    const resetHeight = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.style.height = `${minHeight}px`;
        }
    }, [minHeight]);

    /**
     * Focus the textarea
     */
    const focus = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    /**
     * Blur the textarea
     */
    const blur = useCallback(() => {
        inputRef.current?.blur();
    }, []);

    /**
     * Auto-resize textarea based on content
     */
    const autoResize = useCallback(() => {
        const textarea = inputRef.current;
        if (!textarea) return;

        // Save scroll position
        const scrollPos = textarea.scrollTop;

        // Reset to min height to get accurate scrollHeight
        textarea.style.height = `${minHeight}px`;

        // Calculate constrained height
        const contentHeight = textarea.scrollHeight;
        const finalHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);

        // Apply height
        textarea.style.height = `${finalHeight}px`;

        // Restore scroll for partial scroll
        if (scrollPos > 0) {
            textarea.scrollTop = scrollPos;
        }

        // Auto-scroll to bottom if at max height
        if (contentHeight >= maxHeight) {
            textarea.scrollTop = textarea.scrollHeight;
        }
    }, [minHeight, maxHeight]);

    /**
     * Clear textarea and reset height
     */
    const clear = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.style.height = `${minHeight}px`;
        }
    }, [minHeight]);

    /**
     * Get current value
     * @returns {string}
     */
    const getValue = useCallback(() => {
        return inputRef.current?.value || "";
    }, []);

    return {
        ref: inputRef,
        getTextarea,
        isAvailable,
        resetHeight,
        focus,
        blur,
        autoResize,
        clear,
        getValue,
    };
};

export default useTextareaRef;
